import { useParams } from "react-router-dom";
import React, { useState, useEffect } from 'react'
import { API, Auth } from 'aws-amplify';
import { api3Post } from '../api/api3Client';
import "@aws-amplify/ui-react/styles.css";
import "react-datepicker/dist/react-datepicker.css";
import Loadingmini from '../components/loadingmini';
import RentalSegmentPicker, { useRentalSegments } from '../components/rentalsegmentpicker';
import { Alert, Flex, Divider, View, Text, Heading, Button, TextField, SelectField} from '@aws-amplify/ui-react';

const CLOSE_RESERVATION_CONFIRM_MSG = (resId) =>
  "Close reservation " + resId + "? This completes the rental and finalizes any return charges.";

const NEVER_ACTIVATED_CLOSE_MSG =
  "This reservation was never activated in the system. " +
  "If the driver took the vehicle, we will record activation at the scheduled start time and then close the reservation. " +
  "Choose Confirm only if that is accurate.";

const DRIVER_NEVER_POSSESSION_WARNING_MSG =
  "If the driver never took possession, the rental did not occur. " +
  "That is the same as cancelling the reservation — it is treated as if it never existed, and may reverse charges or issue refunds. " +
  "Do not continue unless you are certain the driver never had the vehicle.";

function formatApiError(e) {
  if (e == null) return 'unknown error';
  if (typeof e === 'string') return e;
  const data = e.response?.data;
  if (data) {
    if (typeof data === 'string' && data.trim()) return data;
    if (data.message) return String(data.message);
    if (data.detail) {
      const detail = data.detail;
      return typeof detail === 'string' ? detail : JSON.stringify(detail);
    }
    if (data.status && data.status !== 'error') return String(data.status);
  }
  if (e.message) return e.message;
  try { return JSON.stringify(e); } catch (_err) { return String(e); }
}

function apiStatusMessage(res) {
  if (!res) return 'unknown error';
  if (typeof res === 'string') return res;
  if (res.message) return res.message;
  if (res.status) return res.status;
  return JSON.stringify(res);
}

function isPaymentSuccess(res) {
  if (!res || typeof res !== 'object') return false;
  if (res._httpError) return false;
  return String(res.status || '').toLowerCase() === 'success';
}

/** Amplify throws on non-2xx; normalize to a body so callers can branch. */
async function agoGet(queryStringParameters) {
  try {
    return await API.get('adminWebTools', '/ago', { queryStringParameters });
  } catch (e) {
    const data = e?.response?.data;
    if (data && typeof data === 'object') {
      return {
        ...data,
        status: data.status || 'error',
        message: data.message || formatApiError(e),
        _httpError: true,
        _httpStatus: e?.response?.status,
      };
    }
    throw e;
  }
}

/**
 * Connect fleets should use combined checkout only for new-app reservations
 * (combined_checkout_attempt / ins_case). Legacy-paid trips stay on passthrough
 * until cutover.
 */
function reservationUsesNewCheckout(getRes) {
  if (!getRes || typeof getRes !== 'object') return false;
  const consumer = String(
    getRes.driverRequirementsSummary?.requirementsConsumer || ''
  ).toLowerCase();
  if (consumer === 'new') return true;
  if (consumer === 'legacy') return false;
  const caseId = getRes.insuranceSummary?.caseId;
  if (caseId != null && caseId !== '') return true;
  return false;
}

/** Portal ?fleets= selection must include the reservation's fleet_code when set. */
function portalFleetSelectionMismatch(resFleetCode) {
  const code = String(resFleetCode || '').trim();
  if (!code) return null;
  let urlFleets = [];
  try {
    const params = new URLSearchParams(window.location.search || '');
    urlFleets = String(params.get('fleets') || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  } catch (_e) {
    return null;
  }
  if (!urlFleets.length) return null;
  const upper = urlFleets.map((f) => f.toUpperCase());
  if (upper.includes(code.toUpperCase())) return null;
  return (
    'error - inconsistent fleet_code between reservation and portal fleets selection: '
    + 'reservation fleet_code=' + code
    + ', portal fleets=' + urlFleets.join(',')
  );
}

export default function Reservationcommands({resId, resData, refreshParent, context}) {
  const [is_loading, updateIsLoading] = React.useState(false);
  const [showCommandResponse, updateShowCommandResponse] = React.useState(true);
  const [command_response, updateCommandResponse] = React.useState(null);
  const [paymentError, updatePaymentError] = React.useState(null);
  const [showConfirm, updateShowConfirm] = React.useState(false);
  const [confirmMessage, updateConfirmMessage] = React.useState(false);
  const [resAction, updateResAction] = React.useState(false);
  const [showForm, updateShowForm] = React.useState(false);
  const [fullRefund, updateFullRefund] = React.useState(true);
  const [cashNotCredits, updateCashNotCredits] = React.useState(true);
  const [switchToCarId, updateSwitchToCarId] = React.useState(0);
  const [moveStartLocal, updateMoveStartLocal] = React.useState("");
  const [showCommandSent, updateShowCommandSent] = React.useState(false);
  const [showPossessionNeverWarning, updateShowPossessionNeverWarning] = React.useState(false);
  const [creditsUsed, updateCreditsUsed] = useState(0);
  const [creditsRemaining, updateCreditsRemaining] = useState(0);
  const [paymentAmountSubTotal, updatePaymentAmountSubTotal] = useState(0);
  const [paymentAmountSubTotalTaxes, updatePaymentAmountSubTotalTaxes] = useState(0);
  const [paymentAmountFinalTotal, updatePaymentAmountFinalTotal] = useState(0);
  const [resCurrentEndDate, updateResCurrentEndDate] = useState("");
  const [resCurrentEndYear, updateResCurrentEndYear] = useState("");
  const [rentalAgreementUrl, updateRentalAgreementUrl] = React.useState(false);
  const [rentalAgreementError, updateRentalAgreementError] = React.useState(false);
  const {
    segments: rentalSegments,
    selectedLedgerVersion,
    setSelectedLedgerVersion,
    loadError: rentalSegmentsError,
  } = useRentalSegments(resId);
  const [extendToDateStringVariable, updateExtendToDateStringVariable] = React.useState("");
  const [owedAmountTotal, updateOwedAmountTotal] = useState(null);
  const [owedAmountSubTotal, updateOwedAmountSubTotal] = useState(null);
  const [owedAmountTaxes, updateOwedAmountTaxes] = useState(null);
  const [owedPreviewError, updateOwedPreviewError] = useState(false);
  const [writeOffReason, updateWriteOffReason] = useState("");
  const [activateMode, updateActivateMode] = useState('now');
  const [isActivated, updateIsActivated] = useState(null);
  const [extendUsesCombinedCheckout, updateExtendUsesCombinedCheckout] = useState(false);
  const [extendInsuranceMode, updateExtendInsuranceMode] = useState('personal');
  const [extendDaysN, updateExtendDaysN] = useState(5);
  const [extendDaysCount, updateExtendDaysCount] = useState(5);

  const isActiveReservation = resData['status'] !== 'cancelled' && resData['status'] !== 'closed' && resData['status'] !== 'owed';
  const isOwedReservation = resData['status'] === 'owed';

  const startInFuture = (() => {
    const unix = Number(resData['start']);
    if (!isNaN(unix) && unix > 0) return unix * 1000 > Date.now();
    const start = new Date(resData['start_string']);
    if (isNaN(start.getTime())) return false;
    return start.getTime() > Date.now();
  })();

  useEffect(() => {
    let cancelled = false;
    async function fetchActivationStatus() {
      try {
        const session = await Auth.currentSession();
        const jwt = session.getAccessToken().getJwtToken();
        const myInit = {queryStringParameters:{
          back_end: process.env.REACT_APP_BACKEND,
          jwt: jwt,
          api_endpoint: "api3:portal:reservation_actions",
          request_type: "GET",
          res_id: resId
        }};
        const res = await API.get('adminWebTools','/ago',myInit);
        const rows = Array.isArray(res?.rows) ? res.rows : [];
        if (!cancelled) updateIsActivated(rows.some((r) => r && r.action === 'activate'));
      } catch (e) {
        if (!cancelled) updateIsActivated(false);
      }
    }
    fetchActivationStatus();
    return () => { cancelled = true; };
  }, [resId, command_response]);

  async function showFormWindow(action) {
    updateIsLoading(true);
    if (action === 'settle' || action === 'waive') {
      updateWriteOffReason("");
      updateOwedAmountTotal(null);
      updateOwedAmountSubTotal(null);
      updateOwedAmountTaxes(null);
      updateOwedPreviewError(false);
    }
    await updateResAction(action);
    updateShowForm(true);
    updateIsLoading(false);
    if (action === 'settle' || action === 'waive') {
      loadOwedPreview();
    }
  }

  async function showConfirmWindow(activateModeArg) {
    updateIsLoading(true);
    var msg = "Are you sure?";
    switch(resAction)
    {
      case 'getcurrent':
          let returnCost = await reservationCurrentCalculate();
          msg = "Are you sure you'd like to get reservation " + resId + " current? It will cost $" + returnCost;
          await updateConfirmMessage(msg);
        break;
      case 'close':
          msg = CLOSE_RESERVATION_CONFIRM_MSG(resId);
          await updateConfirmMessage(msg);
        break;
      case 'closePossession':
          msg = NEVER_ACTIVATED_CLOSE_MSG;
          await updateConfirmMessage(msg);
        break;
      case 'cancel':
          msg = "Are you sure you'd like to cancel  reservation " + resId + " and issue a ";
          if(fullRefund)
            {
              msg = msg + "full refund";
            } else {
              msg = msg + "full refund";
            }
          if(cashNotCredits)
            {
              msg = msg + " to the driver's credit/debit card?";
            } else {
              msg = msg + " in credits to the driver's account?";
            }
          await updateConfirmMessage(msg);
        break;
      case 'switch':
          msg = "Are you sure you'd like to switch reservation " + resId + " to vehicle #" + switchToCarId + "?";
          await updateConfirmMessage(msg);
        break;
      case 'move':
          msg = "Move reservation " + resId + " so it starts " + moveStartLocal + " (car local time)? The length and the insurance window stay the same, and there is no charge. A $0 invoice is emailed.";
          await updateConfirmMessage(msg);
        break;
      case 'activate':
          {
            const mode = activateModeArg || activateMode;
            msg = mode === 'start'
              ? "Activate reservation " + resId + " as of its scheduled start time? This backdates the activation record to the reservation start — use only if the system should have activated it then but didn't. The car will be attached to the reservation."
              : "Activate reservation " + resId + " as of now? The car will be attached to the reservation.";
          }
          await updateConfirmMessage(msg);
        break;
      case 'settle':
          msg = "Charge the driver's default card " + (owedAmountTotal !== null ? "$" + owedAmountTotal : "for the full owed balance") + " and close reservation " + resId + "? This cannot be undone.";
          await updateConfirmMessage(msg);
        break;
      case 'waive':
          msg = "Close reservation " + resId + " WITHOUT charging the driver? The unpaid owed balance will be written off. Reason: " + writeOffReason;
          await updateConfirmMessage(msg);
        break;
      default:
        msg = {'error':'Invalid action selection'}
        await updateConfirmMessage(msg);
        break;
    }
    updateShowConfirm(true);
    updateIsLoading(false);
  }

  async function confirm()
  {
    var res = {}
    updateShowForm(false);
    updateShowConfirm(false);
    updateShowCommandSent(true);
    console.log('resAction:'+resAction)
    switch(resAction)
    {
      case 'getCurrent':
          getResCurrent();
        break;
      case 'cancel':
          cancelReservation();
        break;
      case 'close':
          closeReservation();
        break;
      case 'closePossession':
          closeReservation(true);
        break;
      case 'switch':
          switchCarAssignedToReservation();
        break;
      case 'move':
          moveNewReservation();
        break;
      case 'activate':
          activateReservation();
        break;
      case 'settle':
          settleOwedReservation(true);
        break;
      case 'waive':
          settleOwedReservation(false);
        break;
      default:
        res = {'error':'Invalid action selection'}
        break;
    }
  }


  
  const getResCurrent = async () => {
    updateIsLoading(true)
    let session = await Auth.currentSession()      
    let accessToken = session.getAccessToken()
    let jwt = accessToken.getJwtToken()
    const myInit = {queryStringParameters:{ 
      back_end: process.env.REACT_APP_BACKEND,
      jwt: jwt,
      api_endpoint: "api3:reservations:payments",
      request_type: "POST",
      mode: "payment",
      res_id: resId,
      promo_code: "",
      ago_points: "0",
      payment_type: "extension"
    }};
    try {
      const res = await API.get('adminWebTools','/ago',myInit)
      updateCommandResponse(res);
      updateShowCommandResponse(true);
      refreshParent();
    } catch (e) {
      const res = {status:"Error closing reservation: " + JSON.stringify(e)};
      updateCommandResponse(res);
      updateShowCommandResponse(true);
    }
    updateIsLoading(false);
  }

  const reservationCurrentCalculate = async () => {
    var returnCost = "Error calculating get current"
    updateIsLoading(true)
    const fleetMismatch = portalFleetSelectionMismatch(resData && resData['fleet_code']);
    if (fleetMismatch) {
      updateCommandResponse({ status: fleetMismatch });
      updateShowCommandResponse(true);
      updateIsLoading(false);
      return returnCost;
    }
    var session = await Auth.currentSession()
    var accessToken = session.getAccessToken()
    var jwt = accessToken.getJwtToken()
    try {
      const res = await api3Post(`/reservations/${encodeURIComponent(resId)}/extend`, {
        mode: "penalty",
        overrideLength: true,
      })
      if(res['status']=='success')
      {
        const myInit = {queryStringParameters:{ 
          back_end: process.env.REACT_APP_BACKEND,
          jwt: jwt,
          api_endpoint: "api3:reservations:payments",
          request_type: "POST",
          mode: "compute",
          res_id: resId,
          promo_code: "",
          ago_points: "0",
          payment_type: "extension"
        }};
        try {
          const res = await API.get('adminWebTools','/ago',myInit)
          console.log(JSON.stringify(res))
          returnCost = res['invoice']['total'].toFixed(2)
        } catch (e) {
          const res = {status:"Error getting reservation current: " + JSON.stringify(e)};
          updateCommandResponse(res);
          updateShowCommandResponse(true);
        }
      } else {
        updateCommandResponse(res);
        updateShowCommandResponse(true);
        console.log(JSON.stringify(res));
      }
    } catch (e) {
      const res = {status:"Error calculating get reservation current: " + JSON.stringify(e)};
      updateCommandResponse(res);
      updateShowCommandResponse(true);
    }
    updateIsLoading(false);
    return returnCost;
  }

  const loadOwedPreview = async () => {
    updateOwedAmountTotal(null);
    updateOwedAmountSubTotal(null);
    updateOwedAmountTaxes(null);
    updateOwedPreviewError(false);
    try {
      let session = await Auth.currentSession();
      let accessToken = session.getAccessToken();
      let jwt = accessToken.getJwtToken();
      const myInit = {queryStringParameters:{
        back_end: process.env.REACT_APP_BACKEND,
        jwt: jwt,
        api_endpoint: "api3:reservations:payments",
        request_type: "POST",
        mode: "compute",
        res_id: resId,
        promo_code: "",
        ago_points: "0",
        payment_type: "owed"
      }};
      const res = await API.get('adminWebTools','/ago',myInit);
      if (res['status'] === 'success' && res['invoice']) {
        updateOwedAmountSubTotal(res['invoice']['sub_total'].toFixed(2));
        updateOwedAmountTaxes(res['invoice']['sub_total_taxes'].toFixed(2));
        updateOwedAmountTotal(res['invoice']['total'].toFixed(2));
      } else {
        updateOwedPreviewError(res['status'] || "Could not calculate owed balance");
      }
    } catch (e) {
      updateOwedPreviewError("Could not calculate owed balance: " + JSON.stringify(e));
    }
  };

  const settleOwedReservation = async (chargeDriver) => {
    updateIsLoading(true);
    const reason = chargeDriver ? "portal" : (writeOffReason || "").trim();
    if (!chargeDriver && reason === "") {
      updateCommandResponse({status:"A reason is required to waive the owed balance."});
      updateShowCommandResponse(true);
      updateIsLoading(false);
      return;
    }
    let session = await Auth.currentSession();
    let accessToken = session.getAccessToken();
    let jwt = accessToken.getJwtToken();
    const myInit = {queryStringParameters:{
      back_end: process.env.REACT_APP_BACKEND,
      jwt: jwt,
      api_endpoint: "api3:reservations:settle_owed",
      request_type: "POST",
      res_id: resId,
      charge_driver: chargeDriver,
      reason: reason
    }};
    try {
      const res = await API.get('adminWebTools','/ago',myInit);
      if (res && res['status'] === 'success') {
        const okMsg = chargeDriver
          ? "Success - driver charged and reservation closed."
          : "Success - owed balance waived and reservation closed.";
        updateCommandResponse({status: okMsg});
        updateShowCommandResponse(true);
        refreshParent();
      } else {
        const failMsg = (res && res['status']) ? res['status'] : JSON.stringify(res);
        updateCommandResponse({status: (chargeDriver ? "Could not charge/close: " : "Could not waive/close: ") + failMsg});
        updateShowCommandResponse(true);
      }
    } catch (e) {
      updateCommandResponse({status:"Error settling owed reservation: " + JSON.stringify(e)});
      updateShowCommandResponse(true);
    }
    updateIsLoading(false);
  };

  const closeReservation = async (driverTookPossession) => {
    updateIsLoading(true)
    let session = await Auth.currentSession()      
    let accessToken = session.getAccessToken()
    let jwt = accessToken.getJwtToken()
    const queryStringParameters = { 
      back_end: process.env.REACT_APP_BACKEND,
      jwt: jwt,
      api_endpoint: "api3:reservations:staff_close",
      request_type: "POST",
      res_id: resId,
    };
    if (driverTookPossession === true) {
      queryStringParameters.driver_took_possession = true;
    } else if (driverTookPossession === false) {
      queryStringParameters.driver_took_possession = false;
    }
    const myInit = {queryStringParameters};
    try {
      const res = await API.get('adminWebTools','/ago',myInit)
      console.log('close reservation response:' + JSON.stringify(res));
      if (res && res['status'] === 'needs_possession_confirmation') {
        updateConfirmMessage(NEVER_ACTIVATED_CLOSE_MSG);
        updateResAction('closePossession');
        updateShowPossessionNeverWarning(false);
        updateShowConfirm(true);
        updateShowCommandSent(false);
      } else if (res && res['status'] === 'success') {
        updateCommandResponse({status: "Success — reservation closed."});
        updateShowCommandResponse(true);
        refreshParent();
      } else {
        const failMsg = (res && res['status']) ? res['status'] : JSON.stringify(res);
        updateCommandResponse({status: "Could not close: " + failMsg});
        updateShowCommandResponse(true);
      }
    } catch (e) {
      updateCommandResponse({status:"Error closing reservation: " + JSON.stringify(e)});
      updateShowCommandResponse(true);
    }
    updateIsLoading(false);
  }
  

  const cancelReservation = async () => {
    updateIsLoading(true)
    let session = await Auth.currentSession()      
    let accessToken = session.getAccessToken()
    let jwt = accessToken.getJwtToken()
    const myInit = {queryStringParameters:{ 
      back_end: process.env.REACT_APP_BACKEND,
      jwt: jwt,
      api_endpoint: "api3:reservations:cancel",
      request_type: "POST",
      res_id: resId,
      mode: "cancel",
      started: true,
      full_refund: fullRefund,
      cash_refund: cashNotCredits,
      reason: "Support Portal"
    }};
    try {
      const res = await API.get('adminWebTools','/ago',myInit)
      console.log(JSON.stringify(res))
      let display = res;
      if (res && res.insurance_refund_withheld) {
        const note =
          res.insurance_refund_withheld_message
          || 'Rental refunded; AGO insurance was not refunded because coverage may have started. '
            + 'An AGO admin can refund insurance days from the insurance card.';
        display = Object.assign({}, res, {
          status: (res.status ? String(res.status) + ' — ' : '') + note,
          insurance_note: note,
        });
        window.alert(note);
      }
      updateCommandResponse(display);
      updateShowCommandResponse(true);
      // Parent remount wipes this panel — only refresh after a real cancel.
      if (isPaymentSuccess(res)) {
        refreshParent();
      } else {
        updateShowCommandSent(false);
      }
    } catch (e) {
      const res = {status:"Error computing cancel for reservation: " + JSON.stringify(e)};
      updateCommandResponse(res);
      updateShowCommandResponse(true);
      updateShowCommandSent(false);
    }
    updateIsLoading(false);
  }


  const switchCarAssignedToReservation = async () => {
    updateIsLoading(true)
    let session = await Auth.currentSession()      
    let accessToken = session.getAccessToken()
    let jwt = accessToken.getJwtToken()
    const myInit = {queryStringParameters:{ 
      back_end: process.env.REACT_APP_BACKEND,
      jwt: jwt,
      api_endpoint: "api3:reservations:change_asset",
      request_type: "POST",
      res_id: resId,
      assetId: switchToCarId
    }};
    try {
      const res = await API.get('adminWebTools','/ago',myInit)
      updateCommandResponse(res);
      updateShowCommandResponse(true);
      const okStatus = res && (res.status === 'success' || (res.resp && res.resp.status === 'success'));
      if (okStatus)
      {
        refreshParent();
      }
    } catch (e) {
      const res = {status:"Error: " + JSON.stringify(e)};
      updateCommandResponse(res);
      updateShowCommandResponse(true);
    }
    updateIsLoading(false);
  }

  const moveNewReservation = async () => {
    updateIsLoading(true)
    let session = await Auth.currentSession()
    let accessToken = session.getAccessToken()
    let jwt = accessToken.getJwtToken()
    const myInit = {queryStringParameters:{
      back_end: process.env.REACT_APP_BACKEND,
      jwt: jwt,
      api_endpoint: "api3:reservations:move",
      request_type: "POST",
      res_id: resId,
      newStartLocal: moveStartLocal
    }};
    try {
      const res = await API.get('adminWebTools','/ago',myInit)
      updateCommandResponse(res);
      updateShowCommandResponse(true);
      const okStatus = res && (res.status === 'success' || (res.resp && res.resp.status === 'success'));
      if (okStatus)
      {
        refreshParent();
      }
    } catch (e) {
      const res = {status:"Error: " + JSON.stringify(e)};
      updateCommandResponse(res);
      updateShowCommandResponse(true);
    }
    updateIsLoading(false);
  }

  const activateReservation = async () => {
    updateIsLoading(true)
    let session = await Auth.currentSession()      
    let accessToken = session.getAccessToken()
    let jwt = accessToken.getJwtToken()
    const myInit = {queryStringParameters:{ 
      back_end: process.env.REACT_APP_BACKEND,
      jwt: jwt,
      api_endpoint: "api3:reservations:activate",
      request_type: "POST",
      res_id: resId,
      activate_at: activateMode
    }};
    try {
      const res = await API.get('adminWebTools','/ago',myInit)
      updateCommandResponse(res);
      updateShowCommandResponse(true);
      refreshParent();
    } catch (e) {
      const res = {status:"Error activating reservation: " + JSON.stringify(e)};
      updateCommandResponse(res);
      updateShowCommandResponse(true);
    }
    updateIsLoading(false);
  }
  


  function addDays(date, days) {
    var result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  }


  async function makeandcomputeExtension(extendCheckoutType, daysCount = null)
  {
    updateIsLoading(true);
    //CREATE EXTENSION, CALCULATE COST
    //extendCheckoutType = "hour" or "day" or "week" or "days" (with daysCount 1..7)
    var thisError = "";
    var extendToDate = "";

    const fleetMismatch = portalFleetSelectionMismatch(resData && resData['fleet_code']);
    if (fleetMismatch) {
      updateCommandResponse({ status: fleetMismatch });
      updateShowCommandResponse(true);
      updateIsLoading(false);
      return;
    }

    let dateStringStart = resData['end_string']
    let [datePart, timePart] = dateStringStart.split(" ");
    let [resCurrentEndYear, month, splitDateDay] = datePart.split("-");
    var splitDateMonth = month - 1;
    let [splitDateHour, splitDateMinute] = timePart.split(":");
    console.log(
      `calculating resCurrentEnd...
      ***************
      dateStringStart: ${dateStringStart}
      datePart: ${datePart}
      timePart: ${timePart}
      resCurrentEndYear: ${resCurrentEndYear}
      month: ${month}
      splitDateMonth: ${splitDateMonth}
      splitDateDay: ${splitDateDay}
      splitDateHour: ${splitDateHour}
      ***************`
      );
      
    var resCurrentEnd = new Date(resCurrentEndYear,splitDateMonth,splitDateDay,splitDateHour);
    console.log('resCurrentEnd='+resCurrentEnd);

    extendToDate = resCurrentEnd;
    if(extendCheckoutType=='day')
    {
      console.log('extend day');
      extendToDate = addDays(extendToDate, 1);  // 1 day after currentEndDate
    } else if (extendCheckoutType=='days') {
      const n = parseInt(daysCount, 10);
      if (!Number.isFinite(n) || n < 1 || n > 7) {
        thisError = "error - days must be an integer from 1 to 7";
      } else {
        console.log('extend days n=' + n);
        extendToDate = addDays(extendToDate, n);
      }
    } else {
      if(extendCheckoutType=='week')
      {
        console.log('extend week');
        extendToDate = addDays(extendToDate, 7);  // 1 day after currentEndDate
      } else {
        if(extendCheckoutType=='hour')
        {
          console.log('extend hour');
          if(splitDateHour < 23)
          {
            var newHour = Number(splitDateHour) + 1
            console.log('not starting at 11PM')
            console.log(
              `calculating extendToDate...
              ***************
              resCurrentEndYear: ${resCurrentEndYear}
              splitDateMonth: ${splitDateMonth}
              splitDateDay: ${splitDateDay}
              splitDateHour: ${splitDateHour}
              newHour: ${newHour}
              ***************`
              );
            extendToDate = new Date(resCurrentEndYear,splitDateMonth,splitDateDay,newHour);
            extendToDate = addDays(extendToDate, 0);
          } else {
            extendToDate = new Date(resCurrentEndYear,splitDateMonth,splitDateDay,0);
            extendToDate = addDays(extendToDate, 1);
          }
        } else {
          var thisError = "error - invalid extend checkout type";
        }
      }
    }
    var extendToDateString = extendToDate.toString();
    var extendToDateStringSplit = extendToDateString.split(" GMT");
    extendToDateString = extendToDateStringSplit[0];
    updateExtendToDateStringVariable(extendToDateString);
    console.log("extendToDate="+extendToDate);
    console.log("extendToDateString="+extendToDateString);
    if(thisError=="")
    {
        // FIRST, TRY TO ACTUALLY MAKE THE EXTENSION
        try {
            let session = await Auth.currentSession()
            let accessToken = session.getAccessToken()
            let jwt = accessToken.getJwtToken()
            const pad2 = (n) => String(n).padStart(2, '0');
            const endString = `${extendToDate.getFullYear()}-${pad2(extendToDate.getMonth() + 1)}-${pad2(extendToDate.getDate())} ${pad2(extendToDate.getHours())}:${pad2(extendToDate.getMinutes())}`;
            const res = await api3Post(`/reservations/${encodeURIComponent(resId)}/extend`, {
                endString,
                overrideLength: true,
            });
            //console.log("response from passthrough 1:"+JSON.stringify(res));
            if (res['status'] === 'already_current') {
              updateCommandResponse(res);
              updateShowCommandResponse(true);
              updateIsLoading(false);
              refreshParent();
            } else if (res['status'] == 'success') {
                // Prefer Option C combined checkout only for new-app reservations on
                // Connect fleets; legacy-paid trips keep passthrough until cutover.
                let useCombined = false;
                try {
                  const scRes = await agoGet({
                    back_end: process.env.REACT_APP_BACKEND,
                    jwt: jwt,
                    api_endpoint: "api3:fleets:stripe_connect:get",
                    request_type: "GET",
                    fleetId: resData['fleet_id'],
                  });
                  useCombined = !!(scRes && (scRes.useCombinedCheckout === true || scRes.useCombinedCheckout === 1));
                } catch (_scErr) {
                  useCombined = false;
                }

                if (useCombined) {
                  let isNewAppRes = false;
                  try {
                    const getRes = await agoGet({
                      back_end: process.env.REACT_APP_BACKEND,
                      jwt: jwt,
                      api_endpoint: "api3:reservations:get",
                      request_type: "GET",
                      res_id: resId,
                      id: resId,
                    });
                    isNewAppRes = reservationUsesNewCheckout(getRes);
                  } catch (_getErr) {
                    isNewAppRes = false;
                  }
                  useCombined = isNewAppRes;
                }

                if (useCombined) {
                  const tryModes = ['purchasing', 'personal'];
                  let payRes = null;
                  let usedMode = null;
                  let lastCombinedErr = null;
                  for (const mode of tryModes) {
                    const ccRes = await agoGet({
                      back_end: process.env.REACT_APP_BACKEND,
                      jwt: jwt,
                      api_endpoint: "api3:reservations:checkout_combined",
                      request_type: "POST",
                      res_id: resId,
                      id: resId,
                      mode: "compute",
                      checkoutPhase: "extension",
                      insuranceMode: mode,
                      promoCode: "",
                      agoPoints: 0,
                    });
                    if (ccRes && ccRes.status === 'success' && (ccRes.grandTotalUsd != null || ccRes.rental)) {
                      payRes = ccRes;
                      usedMode = mode;
                      break;
                    }
                    lastCombinedErr = ccRes;
                    const msg = String((ccRes && (ccRes.message || ccRes.status)) || '');
                    // Preference mismatch / no case → try other mode
                    if (!/409|preference|insuranceMode|case|404|not found/i.test(msg) && tryModes.indexOf(mode) === 0) {
                      // keep trying personal as fallback
                    }
                  }
                  if (!payRes) {
                    // Soft-fail to legacy compute so ops can still collect extension rent.
                    console.warn('combined checkout compute failed; falling back to legacy', lastCombinedErr);
                    useCombined = false;
                  } else {
                    const rentalTotal = Number((payRes.rental && payRes.rental.totalUsd) || 0);
                    const insTotal = Number((payRes.insurance && (payRes.insurance.totalUsd || (payRes.insurance.invoice && payRes.insurance.invoice.totalUsd))) || 0);
                    const grand = Number(payRes.grandTotalUsd != null ? payRes.grandTotalUsd : (rentalTotal + insTotal));
                    updateCreditsUsed(0);
                    updateCreditsRemaining(0);
                    updatePaymentAmountSubTotal(rentalTotal.toFixed(2));
                    updatePaymentAmountSubTotalTaxes(insTotal.toFixed(2));
                    updatePaymentAmountFinalTotal(grand.toFixed(2));
                    updateExtendUsesCombinedCheckout(true);
                    updateExtendInsuranceMode(usedMode || 'personal');
                    showFormWindow("extend_confirm");
                  }
                }
                if (!useCombined) {
                // THEN, WE NEED TO COMPUTE PAYMENT passthroughrescomputepayment
                const myInit = {queryStringParameters:{
                    "back_end": process.env.REACT_APP_BACKEND,
                    "jwt":jwt,
                    "api_endpoint":"api3:reservations:payments",
                    "request_type":"POST",
                    "mode":"compute",
                    "res_id":resId,
                    "promo_code":"",
                    "ago_points":"0",
                    "payment_type":"extension"
                }};
                const payRes = await API.get('adminWebTools','/ago',myInit);
                console.log("response from passthrough 2:"+JSON.stringify(payRes));
                if (payRes['status'] !== 'success' || !payRes['invoice']) {
                  updateCommandResponse(typeof payRes === 'object' ? payRes : { status: apiStatusMessage(payRes) });
                  updateShowCommandResponse(true);
                  updateIsLoading(false);
                  return;
                }
                // FINALLY, WE SET ALL THE PAYMENT VARIABLES
                updateCreditsUsed(payRes['invoice']['credits_used'].toFixed(2));
                updateCreditsRemaining(payRes['invoice']['credits_remaining'].toFixed(2));
                updatePaymentAmountSubTotal(payRes['invoice']['sub_total'].toFixed(2));
                updatePaymentAmountSubTotalTaxes(payRes['invoice']['sub_total_taxes'].toFixed(2));
                updatePaymentAmountFinalTotal(payRes['invoice']['total'].toFixed(2));
                updateExtendUsesCombinedCheckout(false);
                showFormWindow("extend_confirm");
                }
                //updateIsLoading(false);
            } else {
              updateCommandResponse(res);
              updateShowCommandResponse(true);
              updateIsLoading(false);
            }
        }
        catch (e) {
            const res = {status:"Error - email us at info@joinago.com: "+formatApiError(e)};
            updateCommandResponse(res);
            updateShowCommandResponse(true);
            updateIsLoading(false);
        }
    } else {
      const res = {status:"Error: "+thisError};
      updateCommandResponse(res);
      updateShowCommandResponse(true);
      updateIsLoading(false);
    }
  }

  async function submitPayment()
  {
      updateIsLoading(true);
      updateShowForm(false);
      updateShowConfirm(false);
      updatePaymentError(null);
      updateCreditsUsed(0);
      updateCreditsRemaining(0);
      updatePaymentAmountSubTotal(0);
      updatePaymentAmountSubTotalTaxes(0);
      updatePaymentAmountFinalTotal(0);
      try {
          let session = await Auth.currentSession()
          let accessToken = session.getAccessToken()
          let jwt = accessToken.getJwtToken()
          let res;
          if (extendUsesCombinedCheckout) {
            res = await agoGet({
              back_end: process.env.REACT_APP_BACKEND,
              jwt: jwt,
              api_endpoint: "api3:reservations:checkout_combined",
              request_type: "POST",
              res_id: resId,
              id: resId,
              mode: "payment",
              checkoutPhase: "extension",
              insuranceMode: extendInsuranceMode || "personal",
              promoCode: "",
              agoPoints: 0,
              idempotencyKey: "portal-extend-" + resId + "-" + Date.now(),
            });
            updateExtendUsesCombinedCheckout(false);
          } else {
            res = await agoGet({
              back_end: process.env.REACT_APP_BACKEND,
              jwt: jwt,
              api_endpoint: "api3:reservations:payments",
              request_type: "POST",
              mode: "payment",
              res_id: resId,
              promo_code: "",
              ago_points: 0,
              payment_type: "extension",
            });
          }
          console.log('completepayment:', res);
          updateCommandResponse(res);
          updateShowCommandResponse(true);
          if (isPaymentSuccess(res)) {
            updatePaymentError(null);
            refreshParent();
          } else {
            // Do not refreshParent on decline — parent remount wipes this message.
            updatePaymentError(apiStatusMessage(res));
          }
      }
      catch (e) {
          console.log('completepayment error:', e);
          const res = {status:"Error: "+formatApiError(e)};
          updateCommandResponse(res);
          updateShowCommandResponse(true);
          updatePaymentError(formatApiError(e));
      }
      updateIsLoading(false);
  }

  

  async function generateRentalAgreement()
  {
      updateIsLoading(true);
      try {
          let session = await Auth.currentSession()
          let accessToken = session.getAccessToken()
          let jwt = accessToken.getJwtToken()
          const myInit = {queryStringParameters:{
              "back_end": process.env.REACT_APP_BACKEND,
              "jwt":jwt,
              "api_endpoint": "api3:staff:rental_agreement",
              "request_type":"GET",
              "res_id":resId,
              ...(selectedLedgerVersion ? { ledger_version: selectedLedgerVersion } : {}),
          }};
          const res = await API.get('adminWebTools','/ago', myInit);
          console.log('rentalagreement:' + JSON.stringify(res));
          if('detail' in res)
          {
            updateRentalAgreementError('An error occured generating the rental agreement, please contact developer@agorisk.com and include your reservation ID. '+res['detail'])
          } else {
            updateRentalAgreementUrl(res['rental_agreement_docx_url']);
          }
      }
      catch (e) {
          console.log('error: '+formatApiError(e))
      }
      updateIsLoading(false);
  }

  return (
    is_loading ? <Loadingmini /> : 
      <Flex
        direction="column"
      >
        <View style={{padding: context ? "0" : "1rem", width:"100%"}}>
          {context ? null : <Divider />}
          <Flex direction="column" gap="1rem" >
            {context ? null : <Heading level={2}>Reservation Commands</Heading>}
            {
              showCommandSent ?
                <View>
                  <Heading level={4}>Command Sent</Heading>
                  <Text>View the Server Response below</Text>
                  <br/>
                  <br/>
                  <Button onClick={() => updateShowCommandSent(false)}>
                        EXIT - SHOW COMMANDS
                  </Button>
                </View>
              :
              showPossessionNeverWarning ?
                <View>
                  <Heading level={4}>Rental never occurred?</Heading>
                  <Text style={{maxWidth:"350px"}}>{DRIVER_NEVER_POSSESSION_WARNING_MSG}</Text>
                  <br/>
                  <Button variation="primary" onClick={() => {
                    updateShowPossessionNeverWarning(false);
                    updateConfirmMessage(NEVER_ACTIVATED_CLOSE_MSG);
                    updateResAction('closePossession');
                    updateShowConfirm(true);
                  }}>
                      Go back
                  </Button>
                  <br/>
                  <br/>
                  <Button onClick={() => {
                    updateShowPossessionNeverWarning(false);
                    updateShowConfirm(false);
                    updateCommandResponse({
                      status: "Close stopped. Use Cancel Reservation if the driver never took the vehicle — that treats the booking as if it never existed and may trigger refunds or other billing changes.",
                    });
                    updateShowCommandResponse(true);
                  }}>
                      Yes, I'm sure — use Cancel Reservation instead
                  </Button>
                </View>
              :
              showConfirm ? 
                <View>
                  <Heading level={4}>{resAction === 'closePossession' ? 'Activation missing' : 'Are you sure?'}</Heading>
                  <Text style={{maxWidth:"350px"}}>{confirmMessage}</Text>
                  <br/>
                  {resAction === 'closePossession' ? (
                    <>
                      <Button variation="primary" onClick={() => confirm()}>
                          Confirm
                      </Button>
                      <br/>
                      <br/>
                      <Button onClick={() => {
                        updateShowConfirm(false);
                        updateShowPossessionNeverWarning(true);
                      }}>
                          Driver never took possession
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button variation="primary" onClick={() => confirm()}>
                          Confirm
                      </Button>
                      <br/>
                      <br/>
                      <Button onClick={() => updateShowConfirm(false)}>
                          Exit
                      </Button>
                    </>
                  )}
                </View>
              : 
              showForm ? 
                <Flex direction="column" gap="1rem">
                  {
                    resAction==='extend' ? 
                    <Flex direction="column">
                      <Heading level={4}>Extend Reservation</Heading>
                      <Text>Extend the reservation and automatically collect payment (uses credits if available).</Text>
                      <Text><i>Hint: Requires the driver is not quiet suspended, and is unavailable beyond a grace period.</i></Text>
                      <Button onClick={() => makeandcomputeExtension("hour")}>
                          ADD 1 HOUR
                      </Button>
                      <Button onClick={() => makeandcomputeExtension("day")}>
                          ADD 1 DAY
                      </Button>
                      <Button onClick={() => makeandcomputeExtension("week")}>
                          ADD 1 WEEK
                      </Button>
                      <Flex direction="row" alignItems="flex-end" gap="0.5rem" wrap="wrap">
                        <SelectField
                          label="Days (1–7)"
                          value={String(extendDaysN)}
                          onChange={(e) => updateExtendDaysN(Number(e.target.value))}
                        >
                          {[1, 2, 3, 4, 5, 6, 7].map((n) => (
                            <option key={n} value={n}>{n}</option>
                          ))}
                        </SelectField>
                        <Button onClick={() => makeandcomputeExtension("days", extendDaysN)}>
                          ADD {extendDaysN} DAY{extendDaysN === 1 ? '' : 'S'}
                        </Button>
                      </Flex>
                    </Flex>
                    : resAction==='extend_confirm' ? 
                    <Flex direction="column">
                      <Heading level={4}>Confirm & Pay</Heading>
                      <Text>Use the default card (and any available credits) to complete payment and extend reservation until {extendToDateStringVariable}.
                        {extendUsesCombinedCheckout ? ' (Combined checkout — rental + insurance if applicable.)' : ''}
                        {extendUsesCombinedCheckout ? ` Subtotal shows rental; taxes line shows insurance/service portion.` : ''}
                      </Text>
                      <Text><i>Hint: Requires the driver is not quiet suspended, and is unavailable beyond a grace period.</i></Text>
                      {
                        Math.abs(creditsUsed) > 0 ? <View>
                            <Text>credits to use: ${Math.abs(creditsUsed)}</Text>
                            <Text>credits remaining: ${creditsRemaining}</Text>
                        </View> : undefined
                      }
                      <Text>Sub Total: ${paymentAmountSubTotal}</Text>
                      <Text>Taxes: ${paymentAmountSubTotalTaxes}</Text>
                      <Text>Total: ${paymentAmountFinalTotal}</Text>
                      <Button onClick={() => submitPayment()}>
                          SUBMIT & PAY
                      </Button>
                    </Flex>
                    : resAction==='move' ?
                    <Flex direction="column">
                      <Heading level={4}>Move new reservation</Heading>
                      <Text>Moves the start to another whole hour. The rental length and the insurance window stay the same, so the price does not change. A $0 invoice is emailed.</Text>
                      <Text>Current start: {resData['start_string']}. Current end: {resData['end_string']}.</Text>
                      <TextField
                          value={moveStartLocal}
                          label="New start (car local time)"
                          descriptiveText="Whole hour, at least one hour from now and within a week. Example: 2026-11-02 16:00"
                          onChange={(e) => updateMoveStartLocal(e.target.value)}
                      />
                      <Button isDisabled={!moveStartLocal.trim()} onClick={() => showConfirmWindow()}>
                          MOVE
                      </Button>
                    </Flex>
                  : resAction==='getcurrent' ? 
                    <Flex direction="column">
                      <Heading level={4}>Get Reservation Current</Heading>
                      <Text>Charge late fees and extend so reservation is about to end.</Text>
                      <Text><i>Hint: This is helpful when a driver is late returning the vehicle but wants to continue driving.</i></Text>
                      <Button onClick={() => showConfirmWindow()}>
                          SUBMIT
                      </Button>
                    </Flex>
                  : resAction==='close' ? 
                    <Flex direction="column">
                      <Heading level={4}>Close Reservation</Heading>
                      <Text>If a reservation did not automatically close, you can manually close it here.</Text>
                      <Text><i>Hint: Helpful when vehicle GPS was unavailable at return.</i></Text>
                      <Button onClick={() => showConfirmWindow()}>
                          Submit
                      </Button>
                    </Flex>
                  : resAction==='cancel' ? 
                    <Flex direction="column">
                      <Heading level={4}>Cancel Reservation</Heading>
                      <Text>Do not cancel reservations that actually occured.</Text>
                      {fullRefund == true ? 
                          <Text style={{textAlign:'center'}}>
                            <Button style={{border:'none', fontStyle: 'italic'}} onClick={() => updateFullRefund(false)}>Issue a Full Refund (click to change)</Button>
                          </Text>
                        : 
                          <Text style={{textAlign:'center'}}>
                            <Button style={{border:'none', fontStyle: 'italic'}} onClick={() => updateFullRefund(true)}>Do not refund deposit (click to change)</Button>
                          </Text>
                      }
                      {cashNotCredits == true ? 
                          <Text style={{textAlign:'center'}}>
                            <Button style={{border:'none', fontStyle: 'italic'}} onClick={() => updateCashNotCredits(false)}>Issue Refund to Card (click to change)</Button>
                          </Text>
                        : 
                          <Text style={{textAlign:'center'}}>
                            <Button style={{border:'none', fontStyle: 'italic'}} onClick={() => updateCashNotCredits(true)}>Issue Refund in Credits (click to change)</Button>
                          </Text>
                      }
                      <Button onClick={() => confirm()}>
                          SUBMIT
                      </Button>
                    </Flex>
                  : resAction==='switch' ? 
                    <Flex direction="column">
                      <Heading level={4}>Switch Car Assigned to Reservation</Heading>
                      <Text><TextField
                          value={switchToCarId}
                          label="New Car ID"
                          descriptiveText="Car ID of the new car"
                          hasError={parseInt(switchToCarId) < 1 || switchToCarId==="" ? true : false}
                          labelHidden={parseInt(switchToCarId) < 1 || switchToCarId==="" ? false : true}
                          errorMessage="This is a required field"
                          placeholder="1234"
                          onChange={(e) => updateSwitchToCarId(e.currentTarget.value)}
                      /></Text>
                      <Button onClick={() => showConfirmWindow("close")}>
                          SUBMIT
                      </Button>
                    </Flex>
                  : 
                  resAction==='activate' ? 
                    <Flex direction="column">
                      <Heading level={4}>Activate Reservation</Heading>
                      <Text>If a reservation did not automatically activate, you can manually activate it here. Either choice attaches the car to the reservation.</Text>
                      <Text><i>Hint: This is a good way to troubleshoot why a driver doesn't have keys they should.</i></Text>
                      <Button onClick={() => { updateActivateMode('now'); showConfirmWindow('now'); }}>
                          ACTIVATE NOW
                      </Button>
                      <Text style={{textAlign:'center'}}><i>Records activation as of now.</i></Text>
                      <Button onClick={() => { updateActivateMode('start'); showConfirmWindow('start'); }}>
                          ACTIVATE AS OF RESERVATION START
                      </Button>
                      <Text style={{textAlign:'center'}}><i>Backdates the activation record to the scheduled start — use if the system should have activated it at the start but didn't.</i></Text>
                    </Flex>
                  : resAction==='settle' ?
                    <Flex direction="column">
                      <Heading level={4}>Settle Owed Balance</Heading>
                      <Text>Charge the driver's default card for the full owed balance (uses available credits first) and close the reservation.</Text>
                      {
                        owedPreviewError ?
                          <View>
                            <Text style={{color:'#B71C1C'}}>Could not calculate the owed balance: {String(owedPreviewError)}</Text>
                            <Text><i>You can still charge - the exact amount is computed when the charge runs.</i></Text>
                            <Button onClick={() => loadOwedPreview()}>RETRY CALCULATION</Button>
                          </View>
                        : owedAmountTotal === null ?
                          <Text>Calculating owed balance...</Text>
                        :
                          <View>
                            <Text>Sub Total: ${owedAmountSubTotal}</Text>
                            <Text>Taxes: ${owedAmountTaxes}</Text>
                            <Text><b>Total to charge: ${owedAmountTotal}</b></Text>
                          </View>
                      }
                      <Button onClick={() => showConfirmWindow()}>
                          CHARGE CARD &amp; CLOSE
                      </Button>
                    </Flex>
                  : resAction==='waive' ?
                    <Flex direction="column">
                      <Heading level={4}>Waive Owed Balance</Heading>
                      <Text>Close the reservation WITHOUT charging the driver. The unpaid fees will be written off (the driver's card is not charged).</Text>
                      {
                        owedPreviewError ?
                          <Text style={{color:'#B71C1C'}}>Owed balance estimate unavailable: {String(owedPreviewError)}</Text>
                        : owedAmountTotal === null ?
                          <Text>Calculating owed balance...</Text>
                        :
                          <Text><i>Estimated owed balance being waived: ${owedAmountTotal}</i></Text>
                      }
                      <TextField
                        value={writeOffReason}
                        label="Reason for waiving (required)"
                        descriptiveText="Explain why the owed balance is being forgiven"
                        onChange={(e) => updateWriteOffReason(e.currentTarget.value)}
                      />
                      <Button
                        onClick={() => showConfirmWindow()}
                        isDisabled={!writeOffReason || writeOffReason.trim() === ""}
                      >
                          WAIVE &amp; CLOSE
                      </Button>
                    </Flex>
                  :
                    <Flex direction="column">
                      <Heading level={4}>Invalid Selection</Heading>
                      <Text>{resAction} is not a recognized selection</Text>
                    </Flex>
                  }
                  <br/>
                  <Button onClick={() => updateShowForm(false)}>
                        EXIT
                  </Button>
                </Flex>
              : 
              <Flex direction="column" gap="1rem">
                {
                  resData['status'] !== 'cancelled' ? undefined :
                  context ?
                    <Text className="staff-command-note">This reservation is cancelled. There is no action to take.</Text>
                  :
                    <Flex direction="column">
                      <Button style={{backgroundColor:'#9E9E9E'}}>
                          CANNOT ACT ON CANCELLED RESERVATION
                      </Button>
                    </Flex>
                }
                {
                  resData['status'] !== 'closed' ? undefined :
                  context ?
                    <Text className="staff-command-note">This reservation is closed. There is no action to take.</Text>
                  :
                    <Flex direction="column">
                      <Button style={{backgroundColor:'#9E9E9E'}}>
                          CANNOT ACT ON CLOSED RESERVATION
                      </Button>
                    </Flex>
                }
                {
                  !isOwedReservation ? undefined :
                    <Text style={{color:'#E65100', fontWeight:'bold', padding:'0.25rem 0'}}>
                      &#9888; This reservation has an unpaid balance (owed). Choose an action below.
                    </Text>
                }
                {
                  !isOwedReservation ? undefined :
                  <Flex direction="column">
                    {context ? <Text className="staff-command-note">Collect the unpaid balance on this reservation.</Text> : null}
                    <Button onClick={() => showFormWindow("settle")}>
                        SETTLE OWED BALANCE
                    </Button>
                  </Flex>
                }
                {
                  !isOwedReservation ? undefined :
                  <Flex direction="column">
                    {context ? <Text className="staff-command-note">Clear the unpaid balance without collecting it.</Text> : null}
                    <Button onClick={() => showFormWindow("waive")}>
                        WAIVE OWED BALANCE
                    </Button>
                  </Flex>
                }
                {
                  !isActiveReservation ? undefined :
                  <Flex direction="column">
                    {context ? <Text className="staff-command-note">{"Ends the rental and finalizes return charges. Status is " + resData['status'] + "."}</Text> : null}
                    <Button onClick={() => showFormWindow("close")}>
                        CLOSE RESERVATION
                    </Button>
                  </Flex>
                }
                {
                  !isActiveReservation ? undefined :
                  <Flex direction="column">
                    {context ? <Text className="staff-command-note">Use this if the driver never took the car. It can reverse charges.</Text> : null}
                    <Button onClick={() => showFormWindow("cancel")}>
                        CANCEL RESERVATION
                    </Button>
                  </Flex>
                }
                {
                  !isActiveReservation ? undefined :
                  <Flex direction="column">
                    {context ? <Text className="staff-command-note">{"Current car: " + (context.carName || "not named") + "."}</Text> : null}
                    <Button onClick={() => showFormWindow("switch")}>
                        SWITCH CAR
                    </Button>
                  </Flex>
                }
                {
                  !(isActiveReservation && isActivated === false && startInFuture) ? undefined :
                  <Flex direction="column">
                    {context ? <Text className="staff-command-note">Moves the start. The length and insurance stay the same.</Text> : null}
                    <Button onClick={() => showFormWindow("move")}>
                        MOVE NEW RESERVATION
                    </Button>
                  </Flex>
                }
                {
                  !isActiveReservation ? undefined :
                  isActivated === true ?
                    context ?
                      <Text className="staff-command-note">{"Already activated. Status is " + resData['status'] + "."}</Text>
                    :
                    <Flex direction="column">
                      <Button isDisabled={true} style={{backgroundColor:'#9E9E9E'}}>
                          ACTIVATE RESERVATION (ALREADY ACTIVATED)
                      </Button>
                    </Flex>
                  :
                  startInFuture ?
                    context ?
                      <Text className="staff-command-note">{"Starts " + (context.startString || resData['start_string']) + ". Activation waits until then."}</Text>
                    :
                    <Flex direction="column">
                      <Button isDisabled={true} style={{backgroundColor:'#9E9E9E'}}>
                          ACTIVATE RESERVATION (NOT STARTED YET)
                      </Button>
                    </Flex>
                  :
                    <Flex direction="column">
                      {context ? <Text className="staff-command-note">The rental has started and is not activated yet.</Text> : null}
                      <Button onClick={() => showFormWindow("activate")}>
                          ACTIVATE RESERVATION
                      </Button>
                    </Flex>
                }
                {
                  /*
                  resData['status'] === 'cancelled' ? undefined :
                  resData['status'] === 'closed' ? undefined :
                  resData['end'] > (Date.now()/1000) ? undefined :
                    <Flex direction="column">
                      <Button onClick={() => showFormWindow("getcurrent")}>
                          GET RESERVATION CURRENT
                      </Button>
                    </Flex>
                  */
                }
                {
                  !isActiveReservation ? undefined :
                    <Flex direction="column">
                      {context ? <Text className="staff-command-note">{"Current end: " + (context.endString || resData['end_string']) + "."}</Text> : null}
                      <Button onClick={() => showFormWindow("extend")}>
                          EXTEND
                      </Button>
                    </Flex>
                }
                {
                  rentalAgreementError ?
                    <Flex direction="column">
                      <Text>{rentalAgreementError}</Text> 
                      <br/>
                      <Button onClick={() => generateRentalAgreement()}>
                          TRY AGAIN
                      </Button>
                    </Flex>
                  :
                  rentalAgreementUrl === false ? 
                    <Flex direction="column" gap="0.5rem">
                      {context ? <Text className="staff-command-note">Each paid extension is its own contract. Pick the one to print.</Text> : null}
                      <RentalSegmentPicker
                        segments={rentalSegments}
                        selectedLedgerVersion={selectedLedgerVersion}
                        onChange={setSelectedLedgerVersion}
                        loadError={rentalSegmentsError}
                      />
                      <Button onClick={() => generateRentalAgreement()}>
                          GENERATE RENTAL AGREEMENT
                      </Button>
                    </Flex> 
                  : 
                    <Flex direction="column">
                      <p><a href={rentalAgreementUrl} rel='noreferrer' target='_blank'>DOWNLOAD RENTAL AGREEMENT</a></p>
                    </Flex>
                }
              </Flex>
            }
            <Divider />
            {paymentError ? (
              <Alert variation="error" isDismissible={false} marginBottom="1rem">
                Payment failed: {paymentError}
              </Alert>
            ) : command_response && !isPaymentSuccess(command_response) ? (
              <Alert variation="error" isDismissible={false} marginBottom="1rem">
                {apiStatusMessage(command_response)}
              </Alert>
            ) : null}
            {
              command_response == null ? undefined : 
              showCommandResponse === false ? 
                  <Flex direction="column">
                    <Divider />
                    <Button onClick={() => updateShowCommandResponse(true)}>
                      SHOW SERVER RESPONSE
                    </Button>
                    <Divider />
                  </Flex>
              :
                  <Flex direction="column">
                    <Divider />
                    <Heading level={4}>Server Response</Heading>
                    <Flex direction="column">
                        <Text style={{maxWidth:"350px"}}>{JSON.stringify(command_response)}</Text>
                    </Flex>
                    <Button onClick={() => updateShowCommandResponse(false)}>
                      HIDE SERVER RESPONSE
                    </Button>
                    <Divider />
                  </Flex>
            }
          </Flex>
        </View>
      </Flex>
  );
}